import { CountryWithMCC, useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import { Title, Traveler, User } from '@/src/types/itinerary'
import { ValidationState } from '@/src/utils/itinerary/travelerInputValidationRules'
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React, { memo, useRef, useState } from 'react'
import ItineraryAutoComplete from '../itinerary/auto-complete'
import DateSelectorInput from '../itinerary/itinearyDateSelectorInput'
import ItineraryTextField from '../itinerary/itinearyTextField'
import SavedTravelerList, { SavedTravelerListProps } from './savedTravelerList'

export interface PersonalDetailsFormProps {
    idx: number
    titles: Title[]
    days: number[]
    months: string[]
    years: number[]
    traveler: Traveler
    validationState?: ValidationState
    onChangeTravelerDetails: <K extends keyof Traveler>(idx: number, field: K, value: Traveler[K]) => void
    onFieldBlur?: (fieldKey: string, inputValue?: any) => void
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    isDateOfBirthRequired: boolean
    isBirthCountryRequired: boolean
    onSavedTravelerSelect: SavedTravelerListProps['onSavedTravelerSelect']
    userTravellerData: User | undefined
}

const PersonalDetailsForm: React.FC<PersonalDetailsFormProps> = ({
    idx,
    titles,
    days,
    months,
    years,
    traveler,
    validationState,
    onChangeTravelerDetails,
    onFieldBlur,
    isNationalityRequiredToRenderInUserPersonalDetailsSection,
    isDateOfBirthRequired,
    isBirthCountryRequired,
    onSavedTravelerSelect,
    userTravellerData,
}) => {
    const formTranslate = useTranslations('form')
    const commonTranslate = useTranslations('common')
    const titlesTranslate = useTranslations('common.titles')
    const locale = useLocale()
    const { data: countryData, isLoading: isCountryDataLoading } = useGetCountryDataQuery()
    const firstNameRef = useRef<HTMLDivElement>(null)
    const savedTravelerFirstNameRef = useRef<null | string>(null)
    const [openSavedTravelerList, setOpenSavedTravelerList] = useState(false)
    // Helper function to get field validation state
    const getFieldValidation = (fieldName: string) => {
        const fieldKey = `traveler_${idx}_${fieldName}`
        return validationState?.[fieldKey] || { isError: false, errorMessage: '', touched: false }
    }

    const selectedNationality = React.useMemo(
        () => countryData?.find(v => v.en === traveler.nationality),
        [countryData, traveler.nationality]
    )
    const selectedBirthCountry = React.useMemo(
        () => countryData?.find(v => v.en === traveler.birthCountry),
        [countryData, traveler.birthCountry]
    )

    const filteredYears = React.useMemo(() => {
        const currentYear = new Date().getFullYear()
        if (traveler.paxType === 'ADT') {
            const minYear = currentYear - 100
            const maxYear = currentYear - 12
            return years.filter(y => y >= minYear && y <= maxYear)
        }
        if (traveler.paxType === 'CHD') {
            const minYear = currentYear - 12
            const maxYear = currentYear - 2
            return years.filter(y => y >= minYear && y <= maxYear)
        }
        if (traveler.paxType === 'INF') {
            const minYear = currentYear - 2
            const maxYear = currentYear
            return years.filter(y => y >= minYear && y <= maxYear)
        }
        return years
    }, [traveler.paxType, years])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexWrap: 'wrap' }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    height: 'fit-content',
                    flexWrap: 'wrap'
                }}
            >
                {/* Title */}
                <Box sx={{ flex: 1 }}>
                    <InputLabel
                        shrink
                        htmlFor={`title-${idx}`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {formTranslate('title')}
                    </InputLabel>
                    <FormControl
                        fullWidth
                        size="small"
                        error={getFieldValidation('title').isError && getFieldValidation('title').touched}
                    >
                        <Select
                            id={`title-${idx}`}
                            value={traveler.title}
                            onChange={e => {
                                onChangeTravelerDetails?.(idx, 'title', e.target.value as Title)
                            }}
                            onBlur={() => onFieldBlur?.(`traveler_${idx}_title`)}
                            displayEmpty
                            renderValue={selected => {
                                if (!selected) return formTranslate('title')
                                return titlesTranslate(selected.toLowerCase() as 'mr' | 'ms' | 'mrs')
                            }}
                            sx={{
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                fontSize: 14,
                                height: 32,
                                '& .MuiSelect-select': { py: 0.5, px: 1.5 }
                            }}
                            MenuProps={{ PaperProps: { sx: { borderRadius: 1, boxShadow: 3 } } }}
                        >
                            {titles.map(t => (
                                <MenuItem key={t} value={t}>
                                    {titlesTranslate(t.toLowerCase() as 'mr' | 'ms' | 'mrs')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {getFieldValidation('title').touched && getFieldValidation('title').errorMessage && (
                        <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                            {getFieldValidation('title').errorMessage}
                        </Box>
                    )}
                </Box>
                {/* First Name */}
                <Box ref={firstNameRef} sx={{ flex: 2 }}>
                    <InputLabel shrink sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}>
                        {formTranslate('firstNameAndMiddleName')}
                    </InputLabel>
                    <ItineraryTextField
                        placeholder={formTranslate('firstNameAndMiddleName')}
                        sx={{
                            '& .MuiInputBase-input': { py: 0.5, px: 1.5, fontSize: 13, height: 26, borderRadius: 1 },
                            borderRadius: 1
                        }}
                        value={traveler.firstName}
                        onChange={e => {
                            onChangeTravelerDetails?.(idx, 'firstName', e.target.value)
                        }}
                        onBlur={e => {
                            // Check if the related target is within our saved traveler list
                            const relatedTarget = e.relatedTarget as HTMLElement
                            if (relatedTarget && firstNameRef.current?.contains(relatedTarget)) {
                                // 'Blur prevented - clicking within saved traveler list')
                                return
                            }
                            //'Blur allowed - clicking outside saved traveler list'
                            onFieldBlur?.(
                                `traveler_${idx}_firstName`,
                                savedTravelerFirstNameRef?.current || e.target.value || null
                            )
                            savedTravelerFirstNameRef.current = null
                            setOpenSavedTravelerList(false)
                        }}
                        onFocus={() => {
                            setOpenSavedTravelerList(true)
                        }}
                        error={getFieldValidation('firstName').isError && getFieldValidation('firstName').touched}
                        helperText={
                            getFieldValidation('firstName').touched && getFieldValidation('firstName').errorMessage
                        }
                        autoComplete="off"
                    />
                </Box>
                {/* Last Name */}
                <Box sx={{ flex: 2 }}>
                    <InputLabel
                        shrink
                        htmlFor={`last-name-${idx}`}
                        sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                    >
                        {formTranslate('lastName')}
                    </InputLabel>
                    <ItineraryTextField
                        placeholder={formTranslate('lastName')}
                        value={traveler.lastName}
                        onChange={e => {
                            onChangeTravelerDetails?.(idx, 'lastName', e.target.value)
                        }}
                        onBlur={e => onFieldBlur?.(`traveler_${idx}_lastName`, e.target.value)}
                        error={getFieldValidation('lastName').isError && getFieldValidation('lastName').touched}
                        helperText={
                            getFieldValidation('lastName').touched && getFieldValidation('lastName').errorMessage
                        }
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    height: 'fit-content',
                    flexWrap: 'wrap'
                }}
            >
                {/* Nationality */}
                {isNationalityRequiredToRenderInUserPersonalDetailsSection && (
                    <Box sx={{ flex: 1 }}>
                        <ItineraryAutoComplete
                            loading={isCountryDataLoading}
                            label={formTranslate('nationality')}
                            options={countryData || []}
                            groupBy={e => commonTranslate(e.category)}
                            getOptionLabel={option => option[locale as keyof CountryWithMCC]}
                            value={selectedNationality || null}
                            onChange={e => {
                                onChangeTravelerDetails?.(idx, 'nationality', e?.en || '')
                            }}
                            onBlur={() => onFieldBlur?.(`traveler_${idx}_nationality`)}
                            error={
                                getFieldValidation('nationality').isError && getFieldValidation('nationality').touched
                            }
                            errorMessage={
                                (getFieldValidation('nationality').touched &&
                                    getFieldValidation('nationality').errorMessage) ||
                                ''
                            }
                        />
                    </Box>
                )}

                {/* Birth Country */}
                {isBirthCountryRequired && (
                    <Box sx={{ flex: 1 }}>
                        <ItineraryAutoComplete
                            loading={isCountryDataLoading}
                            label={formTranslate('birthCountry')}
                            options={countryData || []}
                            groupBy={e => commonTranslate(e.category)}
                            getOptionLabel={option => option[locale as keyof CountryWithMCC]}
                            value={selectedBirthCountry || null}
                            onChange={e => {
                                onChangeTravelerDetails?.(idx, 'birthCountry', e?.en || '')
                            }}
                            onBlur={() => onFieldBlur?.(`traveler_${idx}_birthCountry`)}
                            error={
                                getFieldValidation('birthCountry').isError && getFieldValidation('birthCountry').touched
                            }
                            errorMessage={
                                (getFieldValidation('birthCountry').touched &&
                                    getFieldValidation('birthCountry').errorMessage) ||
                                ''
                            }
                            placeholder={formTranslate('select')}
                        />
                    </Box>
                )}
                {/* Date of Birth */}
                {isDateOfBirthRequired && (
                    <Box sx={{ flex: 1.5, minWidth: 300, maxWidth: 450 }}>
                        <InputLabel
                            shrink
                            htmlFor={`dob-day-${idx}`}
                            sx={{ fontWeight: 600, fontSize: 16, color: '#374151' }}
                        >
                            {formTranslate('dob')}
                        </InputLabel>
                        <DateSelectorInput
                            dateValue={traveler.dateOfBirth || { day: '', month: '', year: '' }}
                            days={days}
                            months={months}
                            years={filteredYears}
                            onChange={e => {
                                onChangeTravelerDetails?.(idx, 'dateOfBirth', e)
                            }}
                            onBlur={() => onFieldBlur?.(`traveler_${idx}_dateOfBirth`)}
                        />
                        {getFieldValidation('dateOfBirth').touched &&
                            getFieldValidation('dateOfBirth').errorMessage && (
                                <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
                                    {getFieldValidation('dateOfBirth').errorMessage}
                                </Box>
                            )}
                    </Box>
                )}
            </Box>

            <SavedTravelerList
                travelerType={traveler.paxType}
                firstNameRef={firstNameRef}
                openSavedTravelerList={openSavedTravelerList}
                userTravellerData={userTravellerData}
                onSavedTravelerSelect={(idx, savedTraveler) => {
                    onSavedTravelerSelect(idx, savedTraveler)
                    savedTravelerFirstNameRef.current = savedTraveler.personalDetails?.firstName || null
                    // Blur the input after selecting a saved traveler
                    firstNameRef.current?.querySelector('input')?.blur()
                    savedTravelerFirstNameRef.current = null
                }}
                idx={idx}
            />
        </Box>
    )
}

const areEqual = (prevProps: PersonalDetailsFormProps, nextProps: PersonalDetailsFormProps) => {
    if (prevProps.idx !== nextProps.idx) return false

    const prevT = prevProps.traveler
    const nextT = nextProps.traveler

    if (
        prevT !== nextT &&
        (prevT.firstName !== nextT.firstName ||
            prevT.lastName !== nextT.lastName ||
            prevT.title !== nextT.title ||
            prevT.nationality !== nextT.nationality ||
            prevT.birthCountry !== nextT.birthCountry ||
            JSON.stringify(prevT.dateOfBirth) !== JSON.stringify(nextT.dateOfBirth) ||
            prevT.paxType !== nextT.paxType)
    ) {
        return false
    }

    if (
        prevProps.isNationalityRequiredToRenderInUserPersonalDetailsSection !==
            nextProps.isNationalityRequiredToRenderInUserPersonalDetailsSection ||
        prevProps.isDateOfBirthRequired !== nextProps.isDateOfBirthRequired ||
        prevProps.isBirthCountryRequired !== nextProps.isBirthCountryRequired ||
        prevProps.userTravellerData !== nextProps.userTravellerData
    ) {
        return false
    }

    const relevantKeys = [
        `traveler_${prevProps.idx}_title`,
        `traveler_${prevProps.idx}_firstName`,
        `traveler_${prevProps.idx}_lastName`,
        `traveler_${prevProps.idx}_nationality`,
        `traveler_${prevProps.idx}_birthCountry`,
        `traveler_${prevProps.idx}_dateOfBirth`
    ]

    const extract = (state?: ValidationState) =>
        relevantKeys
            .map(k => {
                const v = state?.[k]
                return v ? `${k}:${v.isError ? 1 : 0}:${v.touched ? 1 : 0}:${v.errorMessage || ''}` : `${k}:0:0:`
            })
            .join('|')

    if (extract(prevProps.validationState) !== extract(nextProps.validationState)) return false

    if (
        prevProps.titles !== nextProps.titles ||
        prevProps.days !== nextProps.days ||
        prevProps.months !== nextProps.months ||
        prevProps.years !== nextProps.years
    ) {
        return false
    }

    return true
}

export default memo(PersonalDetailsForm, areEqual)
